import type { LensCardData } from '~/types/leLens';

import { csvCell } from '~/utils/csvCell';

import { type Row, joinedCards, number, ratio, sum, text } from './inventoryValues';

import { sourceAttr } from './semantics';

export const PAGE_ID = 'room-inventory-availability';
export const VIEWS = [
  'High Level Summary',
  'Calendar View',
  'Orphan Room Nights',
  'Salesforce Destination',
  'RM Opportunity',
  'BNBL Room Alert',
  'Low Room Inventory Alert',
] as const;
export const METRICS = [
  ['booked', 'Rooms booked'],
  ['allocated_adjusted', 'Rooms allocated'],
  ['remain_adjusted', 'Rooms remaining'],
  ['booked_rate_calendar', 'Rooms booked %'],
  ['rm_score', 'RM opportunity score'],
  ['missed_rm_score', 'Missed RM opportunity score'],
] as const;
export const FILTERS = [
  ['vendor_name', 'Vendor'],
  ['offer_owner_name', 'Offer owner'],
  ['offer_bk', 'Offer ID'],
  ['offer_opportunity_name', 'Opportunity'],
  ['room_type_name', 'Room type'],
  ['offer_country_name', 'Offer country'],
  ['offer_geo_area_level_1', 'Offer region'],
  ['campaign_type', 'Campaign type'],
  ['manager_type', 'Manager type'],
  ['channel_manager', 'Channel manager'],
  ['deal_region', 'Salesforce region'],
  ['deal_country', 'Salesforce country'],
  ['deal_destination', 'Salesforce state'],
  ['deal_sub_destination', 'Salesforce city'],
  ['blackout', 'Blackout'],
  // Tableau titles this Le Curated quick filter "Live on Website" on every view except High Level Summary.
  ['le_curated', 'Live on website'],
  ['surcharged', 'Surcharged'],
  ['surcharge', 'Surcharge type'],
  ['live_ended', 'Live / ended deal'],
  ['rm_opportunity', 'RM opportunity'],
  ['checkin_available', 'Check-in available'],
  ['latest_room_type', 'Latest room type name'],
  ['availability_year', 'Availability year'],
  ['availability_month', 'Availability month'],
] as const;
// Tableau "Hide Past Months" (default True) applies to these views only: availability dates before the
// as-at snapshot month are hidden unless the user sets it to False.
export const HIDE_PAST_MONTHS_VIEWS: ReadonlyArray<string> = ['Calendar View', 'Orphan Room Nights'];
export function readHidePastMonths(p: URLSearchParams): 'True' | 'False' | 'Past only' {
  const value = p.get('ri_hide_past_months');
  return value === 'False' || value === 'Past only' ? value : 'True';
}
export const SUM_FIELDS = [
  'allocated',
  'allocated_adjusted',
  'booked',
  'remain',
  'remain_adjusted',
  'orphan_rooms',
  'orphan_dates',
  'non_orphan_dates',
  'date_count',
  'lost_revenue',
  'rm_score',
  'missed_rm_score',
  'avg_daily_rate',
  'avg_los',
  'days_since_launch',
  'days_remaining',
  'source_rows',
  'remain_rate_sum',
  'remain_rate_known',
  'days_remaining_known',
];
export function metrics(row: Row): Row {
  const allocated = number(row.allocated_adjusted),
    booked = number(row.booked),
    orphan = number(row.orphan_rooms);
  const available = allocated === null || booked === null || orphan === null ? null : allocated - booked - orphan;
  const bookedRatio = ratio(booked, allocated);
  return {
    ...row,
    booked_rate: bookedRatio,
    booked_rate_calendar: bookedRatio ?? 0,
    booked_including_bnbl:
      booked === null ? null : ratio(booked + (number(row.bnbl_source) ?? 0), number(row.allocated)),
    booked_including_bnbl_adjusted: booked === null ? null : ratio(booked + (number(row.bnbl_source) ?? 0), allocated),
    orphan_allocated_rate: ratio(orphan, allocated),
    orphan_remain_rate: ratio(orphan, number(row.remain_adjusted)),
    orphan_date_rate: ratio(number(row.orphan_dates), number(row.date_count)),
    non_orphan_date_rate: ratio(number(row.non_orphan_dates), number(row.date_count)),
    available_for_sale: available,
    available_rate: ratio(available, allocated) ?? 0,
    average_remain_rate: ratio(number(row.remain_rate_sum), number(row.remain_rate_known)),
    average_days_remaining: ratio(number(row.days_remaining), number(row.days_remaining_known)),
  };
}
export function summary(rows: Array<Row>): Row {
  const max = (k: string) => {
    const vs = rows.map((r) => number(r[k])).filter((n): n is number => n !== null);
    return vs.length ? Math.max(...vs) : null;
  };
  return metrics({
    ...Object.fromEntries(SUM_FIELDS.map((k) => [k, sum(rows, k)])),
    bnbl_source: max('bnbl_source'),
    max_days_since_launch: max('max_days_since_launch'),
    max_days_remaining: max('max_days_remaining'),
    bnbl_vendor: sum(rows, 'bnbl_vendor'),
  });
}
export function unpack(rows: Array<Row>, field: string): Array<Row> {
  return rows.map((r) => {
    let identity: Row;
    try {
      identity = JSON.parse(text(r[field]));
    } catch {
      identity = {};
    }
    return metrics({
      ...identity,
      ...('surcharge_type' in identity
        ? { surcharged: identity.surcharge_type !== null && identity.surcharge_type !== undefined ? 'true' : 'false' }
        : {}),
      ...r,
    });
  });
}
export function detailRows(rows: Array<Row>, view: string): Array<Row> {
  const physical = unpack(rows, 'detail_key'),
    groups = new Map<string, Array<Row>>();
  const keys =
    view === 'Orphan Room Nights'
      ? ['room_type_name', 'availability_day_of_week', 'checkin_available']
      : view === 'RM Opportunity'
        ? ['vendor_name', 'offer_bk', 'room_type_name', 'availability_day_of_week', 'surcharge_type']
        : view === 'Low Room Inventory Alert'
          ? ['vendor_name', 'offer_opportunity_name', 'room_type_name', 'availability_day_of_week']
          : ['vendor_name', 'room_type_name', 'availability_day_of_week'];
  for (const row of physical) {
    const key = JSON.stringify(keys.map((k) => row[k]));
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  return [...groups.entries()].map(([key, rs]) => {
    const dimensions = Object.fromEntries(
      Object.keys(rs[0])
        .filter((k) => !SUM_FIELDS.includes(k))
        .map((k) => [k, new Set(rs.map((r) => JSON.stringify(r[k]))).size === 1 ? rs[0][k] : null]),
    );
    const result = summary(rs);
    result.bnbl_vendor = Math.max(...rs.map((r) => number(r.bnbl_vendor) ?? 0));
    return {
      ...dimensions,
      ...result,
      ...(view === 'Orphan Room Nights'
        ? Object.fromEntries(
            ['vendor_name', 'blackout', 'min_duration'].map((field) => [
              field,
              sourceAttr(rs.map((row) => row[field])),
            ]),
          )
        : {}),
      key,
      source_evidence: rs,
    };
  });
}
export function chosenMetric(row: Row, metric: string) {
  const n = number(row[metric]);
  return n === null
    ? null
    : metric === 'booked_rate_calendar'
      ? Math.round(n * 100) / 100
      : ['rm_score', 'missed_rm_score'].includes(metric)
        ? Math.round(n * 10) / 10
        : n;
}
export function readSelections(p: URLSearchParams) {
  return Object.fromEntries(
    FILTERS.map(([key]) => [
      key,
      p.has('ri_' + key)
        ? p.getAll('ri_' + key).filter((v) => v !== '__ALL__')
        : key === 'live_ended'
          ? ['Live']
          : key === 'blackout'
            ? ['false', 'true']
            : // Availability year defaults to All on every view. The Tableau saved views kept 2024 and 2025 on
              // Orphan Room Nights and RM Opportunity, which the snapshot no longer holds, so those views were empty.
              [],
    ]),
  );
}

export const DETAIL_DIMENSIONS = [
  'vendor_name',
  'offer_bk',
  'offer_opportunity_name',
  'room_type_name',
  'room_type_bk',
  'availability_day_of_week',
  'snapshot_day',
  'surcharge_type',
  'offer_end_date',
  'deal_country',
  'deal_destination',
  'deal_sub_destination',
  'offer_owner_name',
] as const;
export const DETAIL_MEASURES = [
  ...SUM_FIELDS,
  'bnbl_source',
  'bnbl_vendor',
  'max_days_since_launch',
  'max_days_remaining',
];

export const DETAIL_ATTRIBUTES = ['checkin_available', 'min_duration', 'blackout'] as const;

/** Decode complete warehouse batches before any worksheet regrouping or CSV export. */
export function inventoryBatches(
  cards: ReadonlyArray<LensCardData>,
  id: string,
): { rows: Array<Row>; truncated: boolean; problem?: string } {
  const card = cards.find((c) => c.cardId === id);
  const empty = { rows: [] as Array<Row>, truncated: false };
  if (!card || !['ready', 'empty'].includes(card.status)) {
    return { ...empty, problem: card?.message || `${id} evidence is still loading.` };
  }
  try {
    if (card.truncated) {
      throw Error(`${id} evidence exceeds its batch limit. Narrow the filters to export complete data.`);
    }
    const batches = card.rows ?? [];
    const ids = new Set<string>();
    const rows = batches.flatMap((batch) => {
      const batchId = text(batch.batch_id);
      if (ids.has(batchId)) {
        throw Error('Duplicate evidence batch.');
      }
      ids.add(batchId);
      if (typeof batch.payload !== 'string') throw Error('Invalid evidence payload.');
      const parsed: unknown = JSON.parse(batch.payload);
      if (
        !Array.isArray(parsed) ||
        parsed.length !== number(batch.record_count) ||
        parsed.some((row) => !row || typeof row !== 'object' || Array.isArray(row))
      ) {
        throw Error('Invalid evidence batch.');
      }
      if (id !== 'details') return parsed as Array<Row>;
      return (parsed as Array<Row>).map((row) => {
        const dimensions = row.d,
          measures = row.v;
        if (
          !Array.isArray(dimensions) ||
          dimensions.length !== DETAIL_DIMENSIONS.length ||
          dimensions.some((v) => v !== null && typeof v !== 'string') ||
          !Array.isArray(measures) ||
          measures.length !== DETAIL_MEASURES.length ||
          measures.some((v) => v !== null && (typeof v !== 'number' || !Number.isFinite(v)))
        )
          throw Error('Invalid detail evidence schema.');
        const attributes = row.a;
        if (
          attributes !== null &&
          attributes !== undefined &&
          (!Array.isArray(attributes) ||
            attributes.length !== DETAIL_ATTRIBUTES.length ||
            attributes.some((value) => value !== null && typeof value !== 'string'))
        )
          throw Error('Invalid detail attribute schema.');
        if (
          Array.isArray(attributes) &&
          (!['Available', 'Not available'].includes(attributes[0]) ||
            (attributes[2] !== null && !['true', 'false'].includes(attributes[2])))
        )
          throw Error('Invalid detail attribute schema.');
        const identity = Object.fromEntries(DETAIL_DIMENSIONS.map((key, i) => [key, dimensions[i]]));
        if (Array.isArray(attributes))
          DETAIL_ATTRIBUTES.forEach((key, i) => {
            identity[key] = attributes[i];
          });
        return {
          detail_key: JSON.stringify(identity),
          ...Object.fromEntries(DETAIL_MEASURES.map((key, i) => [key, measures[i]])),
        };
      });
    });
    if (batches.some((batch) => number(batch.source_count) !== rows.length)) {
      throw Error('Incomplete evidence population.');
    }
    return { rows, truncated: false };
  } catch (error) {
    return { ...empty, truncated: true, problem: error instanceof Error ? error.message : 'Invalid evidence.' };
  }
}

/** Page-local last-result cache: compare complete envelopes before reusing validated evidence. */
export function createInventoryBatchReader(id: string) {
  type Result = ReturnType<typeof inventoryBatches>;
  let cached: { status: string; truncated: unknown; batches: Array<Row>; result: Result } | undefined;
  return (cards: ReadonlyArray<LensCardData>): Result => {
    const card = cards.find((candidate) => candidate.cardId === id);
    if (!card || !['ready', 'empty'].includes(card.status) || card.truncated) {
      cached = undefined;
      return inventoryBatches(cards, id);
    }
    const batches = card.rows ?? [];
    const same =
      cached &&
      cached.status === card.status &&
      cached.truncated === card.truncated &&
      cached.batches.length === batches.length &&
      batches.every((batch, index) => {
        const previous = cached!.batches[index];
        return ['batch_id', 'payload', 'record_count', 'source_count'].every((key) => batch[key] === previous[key]);
      });
    const result = same ? cached!.result : inventoryBatches(cards, id);
    cached = result.problem
      ? undefined
      : {
          status: card.status,
          truncated: card.truncated,
          result,
          // Adopt the newest envelope's strings, releasing the previous response's payload references.
          batches: batches.map(({ batch_id, payload, record_count, source_count }) => ({
            batch_id,
            payload,
            record_count,
            source_count,
          })),
        };
    return result;
  };
}

export const INVENTORY_CSV_FORMAT = 'room-inventory-csv/v2';
export const EVIDENCE_COLUMN = `source_evidence[d:${DETAIL_DIMENSIONS.join('|')};v:${DETAIL_MEASURES.join('|')};a:${DETAIL_ATTRIBUTES.join('|')}]`;

/** Maximum text part length. Serialization still temporarily holds the largest individual cell/physical record. */
export const INVENTORY_CSV_PART_CHARACTERS = 1024 * 1024;

/** Serialize evidence one physical record at a time, without a second full array or JSON cell string. */
function* inventoryCsvFragments(rows: ReadonlyArray<Row>, columns: Array<string>, context: Row, exportedAt: string) {
  const hasEvidence = columns.includes('source_evidence');
  const exportColumns = hasEvidence
    ? columns.filter((key) => key !== 'detail_key').map((key) => (key === 'source_evidence' ? EVIDENCE_COLUMN : key))
    : columns;
  const fields = ['record_type', 'metadata_key', 'metadata_value', ...exportColumns];
  yield fields.map((key) => csvCell(key)).join(',');
  for (const [key, value] of Object.entries({ format: INVENTORY_CSV_FORMAT, exported_at: exportedAt, ...context })) {
    const metadata: Row = { record_type: 'metadata', metadata_key: key, metadata_value: JSON.stringify(value ?? null) };
    yield '\r\n' + fields.map((field) => csvCell(metadata[field])).join(',');
  }
  for (const row of rows) {
    yield '\r\n';
    for (let i = 0; i < fields.length; i++) {
      if (i) yield ',';
      const field = fields[i];
      if (field === 'record_type') yield csvCell('data');
      else if (field === 'metadata_key' || field === 'metadata_value') yield csvCell('');
      else if (hasEvidence && field === EVIDENCE_COLUMN && Array.isArray(row.source_evidence)) {
        // JSON arrays cannot begin a spreadsheet formula. Quotes still need the same CSV doubling as csvCell.
        yield '"[';
        let first = true;
        for (const physical of row.source_evidence as Array<Row>) {
          if (!first) yield ',';
          first = false;
          const dimensions = JSON.parse(text(physical.detail_key)) as Row;
          const compact = {
            d: DETAIL_DIMENSIONS.map((key) => dimensions[key] ?? null),
            v: DETAIL_MEASURES.map((key) => physical[key] ?? null),
            ...(DETAIL_ATTRIBUTES.some((key) => key in dimensions)
              ? { a: DETAIL_ATTRIBUTES.map((key) => dimensions[key] ?? null) }
              : {}),
          };
          yield JSON.stringify(compact).replace(/"/g, '""');
        }
        yield ']"';
      } else yield csvCell(row[field]);
    }
  }
}

/** Keep text parts bounded independently of population and largest worksheet evidence group. */
export function* inventoryCsvParts(
  rows: ReadonlyArray<Row>,
  columns: Array<string>,
  context: Row = {},
  exportedAt = new Date().toISOString(),
) {
  let pending: Array<string> = [];
  let characters = 0;
  for (const fragment of inventoryCsvFragments(rows, columns, context, exportedAt)) {
    let offset = 0;
    while (offset < fragment.length) {
      let end = Math.min(fragment.length, offset + INVENTORY_CSV_PART_CHARACTERS - characters);
      // Each text part is independently UTF-8 encoded into a Blob: never split a valid surrogate pair.
      if (end < fragment.length && /[\uD800-\uDBFF]/.test(fragment[end - 1]) && /[\uDC00-\uDFFF]/.test(fragment[end]))
        end--;
      const needsWholePair = end === offset;
      if (end > offset) {
        pending.push(fragment.slice(offset, end));
        characters += end - offset;
        offset = end;
      }
      if (characters === INVENTORY_CSV_PART_CHARACTERS || needsWholePair) {
        const part = pending.join('');
        pending = [];
        characters = 0;
        yield part;
      }
    }
  }
  if (pending.length) yield pending.join('');
}

/** Treemap shelves are State × City; Country is an ATTR tooltip, not part of the mark grain. */
export function salesforceCityRows(rows: ReadonlyArray<Row>): Array<Row> {
  const groups = new Map<string, Array<Row>>();
  for (const row of rows) {
    const key = JSON.stringify([row.deal_destination, row.deal_sub_destination]);
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  }
  return [...groups.entries()].map(([key, group]) => ({
    key,
    deal_destination: group[0].deal_destination,
    deal_sub_destination: group[0].deal_sub_destination,
    deal_country: sourceAttr(group.map((row) => row.deal_country)),
    ...summary(group),
  }));
}

/** Preserve first-seen export column order without a population×columns temporary array. */
export function inventoryColumnKeys(rows: ReadonlyArray<Row>): Array<string> {
  const keys = new Set<string>();
  for (const row of rows) for (const key of Object.keys(row)) keys.add(key);
  return [...keys];
}

/** The daily_blackout batch envelope shares a prefix but has a different grain/schema. */
export function inventoryDaily(cards: ReadonlyArray<LensCardData>) {
  return joinedCards(
    cards.filter((card) => card.cardId === 'daily'),
    'daily',
    ['availability_day_of_week'],
  );
}

/** A hierarchy view never replaces or sums its parent aggregates with child aggregates. */
export function inventoryHierarchy(
  parents: ReadonlyArray<Row>,
  rooms: ReadonlyArray<Row>,
  expanded: ReadonlyArray<string>,
) {
  const children = new Map<string, Array<Row>>();
  for (const row of rooms) {
    const vendor = text(row.vendor_name);
    const group = children.get(vendor) ?? [];
    group.push(row);
    children.set(vendor, group);
  }
  return parents.flatMap((parent) => {
    const vendor = text(parent.vendor_name);
    return [
      { row: parent, depth: 0, vendor },
      ...(expanded.includes(vendor)
        ? (children.get(vendor) ?? [])
            .slice()
            .sort((a, b) => text(a.room_type_name).localeCompare(text(b.room_type_name)))
            .map((row) => ({ row, depth: 1, vendor }))
        : []),
    ];
  });
}
